import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { MemberQuickViewInvolvement } from '@/components/shared/member-quick-view-involvement';
import { MemberPhoto } from '@/components/ui/member-photo';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDate } from '@/utils/format';

export interface MemberQuickViewRecord {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  title: string | null;
  membershipNumber: string | null;
  joinedAt: string | null;
  gender: string | null;
  dateOfBirth: string | null;
  placeOfBirth: string | null;
  maritalStatus: string | null;
  occupation: string | null;
  employer: string | null;
  email: string | null;
  phone: string | null;
  alternatePhone: string | null;
  address: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  hometown: string | null;
  hometownRegion: string | null;
  receptionMethod: string | null;
  formerChurch: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: string | null;
  memberType: 'first_timer' | 'visitor' | 'member';
  status: 'active' | 'inactive' | 'transferred' | 'deceased';
  photoUrl: string | null;
  secondaryHidden?: boolean;
}

export const displayMemberName = (
  person: Pick<MemberQuickViewRecord, 'firstName' | 'middleName' | 'lastName'>,
) => [person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ');

export type MemberQuickViewLayout = 'dialog' | 'panel';
export type MemberQuickViewPreviewMode = 'basic' | 'extended';

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid grid-cols-1 gap-0.5 text-body-sm sm:grid-cols-[8.5rem_1fr] sm:gap-2">
    <dt className="text-fg-secondary">{label}</dt>
    <dd className="min-w-0 break-words text-fg">{value}</dd>
  </div>
);

export const MemberQuickViewLoading = ({ layout = 'dialog' }: { layout?: MemberQuickViewLayout }) => (
  <div
    className={
      layout === 'panel'
        ? 'space-y-3'
        : 'flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6'
    }
  >
    <div className="min-w-0 flex-1 space-y-3">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-4 w-52" />
      <Skeleton className="h-4 w-36" />
      <Skeleton className="h-4 w-44" />
    </div>
    {layout === 'dialog' ? (
      <Skeleton className="h-36 w-[108px] shrink-0 self-center sm:self-start" />
    ) : null}
  </div>
);

export const MemberQuickViewContent = ({
  row,
  name,
  canViewContact,
  layout = 'dialog',
  previewMode = 'basic',
}: {
  row: MemberQuickViewRecord;
  name: string;
  canViewContact: boolean;
  layout?: MemberQuickViewLayout;
  previewMode?: MemberQuickViewPreviewMode;
}) => {
  const { t } = useTranslation('people');
  const dash = t('members.detail.emptyValue');
  const show = (value: string | null | undefined) => value?.trim() || dash;
  const showEnum = (namespace: string, value: string | null | undefined) =>
    value ? t(`${namespace}.${value}`, { defaultValue: dash }) : dash;
  const hometown = [row.city, row.region].filter(Boolean).join(', ') || dash;
  const showExtended = previewMode === 'extended';

  return (
    <div className="flex flex-col gap-6">
      <div
        className={
          layout === 'panel'
            ? undefined
            : 'flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6'
        }
      >
        <dl className="min-w-0 flex-1 space-y-3">
          {layout === 'dialog' ? (
            <div className="flex flex-wrap gap-2">
              <Badge variant={row.status === 'active' ? 'success' : 'neutral'}>
                {t(`members.status.${row.status}`)}
              </Badge>
              <Badge variant="neutral">{t(`members.type.${row.memberType}`)}</Badge>
            </div>
          ) : null}
          <DetailRow label={t('members.form.title')} value={show(row.title)} />
          <DetailRow label={t('members.form.membershipNumber')} value={show(row.membershipNumber)} />
          <DetailRow
            label={t('members.form.joinedAt')}
            value={row.joinedAt ? formatDate(row.joinedAt) : dash}
          />
          <DetailRow
            label={t('members.form.gender')}
            value={row.gender ? t(`members.gender.${row.gender}`, { defaultValue: dash }) : dash}
          />
          {row.secondaryHidden ? (
            <p className="text-body-sm text-fg-secondary">{t('members.secondaryHidden')}</p>
          ) : (
            <>
              <DetailRow
                label={t('members.form.dateOfBirth')}
                value={row.dateOfBirth ? formatDate(row.dateOfBirth) : dash}
              />
              <DetailRow label={t('members.form.occupation')} value={show(row.occupation)} />
            </>
          )}
        </dl>
        {layout === 'dialog' ? (
          <div className="w-[7.5rem] shrink-0 self-center sm:self-start">
            <p className="mb-1.5 text-center text-caption text-fg-secondary">
              {t('members.form.sections.photo')}
            </p>
            <MemberPhoto src={row.photoUrl} name={name} size="xl" className="mx-auto" />
          </div>
        ) : null}
      </div>

      {canViewContact ? (
        <div className="space-y-3">
          <h3 className="text-body-sm font-semibold text-fg">{t('members.detail.sections.contact')}</h3>
          <dl className="space-y-3">
            <DetailRow label={t('members.form.phone')} value={show(row.phone)} />
            <DetailRow label={t('members.form.email')} value={show(row.email)} />
            <DetailRow label={t('members.form.city')} value={hometown} />
          </dl>
        </div>
      ) : null}

      {showExtended && !row.secondaryHidden ? (
        <div className="space-y-3">
          <h3 className="text-body-sm font-semibold text-fg">{t('members.detail.sections.identity')}</h3>
          <dl className="space-y-3">
            <DetailRow label={t('members.form.placeOfBirth')} value={show(row.placeOfBirth)} />
            <DetailRow
              label={t('members.form.maritalStatus')}
              value={showEnum('members.marital', row.maritalStatus)}
            />
            <DetailRow label={t('members.form.employer')} value={show(row.employer)} />
          </dl>
        </div>
      ) : null}

      {showExtended && canViewContact ? (
        <div className="space-y-3">
          <h3 className="text-body-sm font-semibold text-fg">{t('members.preview.extendedContact')}</h3>
          <dl className="space-y-3">
            <DetailRow label={t('members.form.alternatePhone')} value={show(row.alternatePhone)} />
            <DetailRow label={t('members.form.residentialAddress')} value={show(row.address)} />
            <DetailRow label={t('members.form.country')} value={show(row.country)} />
            <DetailRow label={t('members.form.hometown')} value={show(row.hometown)} />
            <DetailRow label={t('members.form.hometownRegion')} value={show(row.hometownRegion)} />
          </dl>
        </div>
      ) : null}

      {showExtended && !row.secondaryHidden ? (
        <div className="space-y-3">
          <h3 className="text-body-sm font-semibold text-fg">{t('members.detail.sections.membership')}</h3>
          <dl className="space-y-3">
            <DetailRow
              label={t('members.form.receptionMethod')}
              value={showEnum('members.reception', row.receptionMethod)}
            />
            <DetailRow label={t('members.form.formerChurch')} value={show(row.formerChurch)} />
          </dl>
        </div>
      ) : null}

      {showExtended && canViewContact ? (
        <div className="space-y-3">
          <h3 className="text-body-sm font-semibold text-fg">{t('members.detail.sections.emergency')}</h3>
          <dl className="space-y-3">
            <DetailRow
              label={t('members.form.emergencyContactName')}
              value={show(row.emergencyContactName)}
            />
            <DetailRow
              label={t('members.form.emergencyContactPhone')}
              value={show(row.emergencyContactPhone)}
            />
            <DetailRow
              label={t('members.form.emergencyContactRelationship')}
              value={show(row.emergencyContactRelationship)}
            />
          </dl>
        </div>
      ) : null}

      {showExtended ? <MemberQuickViewInvolvement memberId={row.id} /> : null}
    </div>
  );
};
